import * as AgGrid from "ag-grid-community";
import * as datasource from "grid/datasource";
import * as gridUtils from "grid/utils";
import { DEFAULT_COL_DEF, ACTIONS_COL_DEF } from "../../grid/columns";
import { GridController } from "grid/grid_controller";
import { SettlementReport } from "../../types/settlement_report";
import * as http from "../../utils/http";
import * as notifications from "utils/notifications";
import { triggerBrowserDownload } from "../../utils/download";
import { parseError } from "utils/responses";

class SelectDownloadableRows implements AgGrid.IHeaderComp {
  eGui!: HTMLElement;
  params!: AgGrid.IHeaderParams;
  eCheckbox!: HTMLInputElement;

  init(params: AgGrid.IHeaderParams): void {
    this.params = params;
    this.eGui = document.createElement("div");
    this.eGui.innerHTML = `<input class="ag-input-field-input ag-checkbox-input" type="checkbox" tabindex="-1">`;
    this.eCheckbox = this.eGui.querySelector("input") as HTMLInputElement;
    this.eCheckbox.addEventListener("change", this.selectDownloadableRows.bind(this));
    this.params.api.addEventListener("paginationChanged", this.updateCheckboxState.bind(this));
    this.params.api.addEventListener("selectionChanged", this.updateCheckboxState.bind(this));
  }

  getGui(): HTMLElement {
    return this.eGui;
  }

  private selectDownloadableRows() {
    this.params.api.getRenderedNodes().forEach((node) => {
      if (node.data?.downloadable) {
        node.setSelected(this.eCheckbox.checked);
      }
    });
  }

  private updateCheckboxState() {
    const downloadableNodes = this.params.api.getRenderedNodes().filter((node) => node.data?.downloadable);
    this.eCheckbox.checked =
      downloadableNodes.length > 0 && downloadableNodes.every((node) => node.isSelected());
  }

  refresh(): boolean {
    return false;
  }

  destroy() {
    this.eCheckbox.removeEventListener("change", this.selectDownloadableRows);
    this.params.api.removeEventListener("paginationChanged", this.updateCheckboxState);
    this.params.api.removeEventListener("selectionChanged", this.updateCheckboxState);
  }
}

function statusCellStyle(status: string | undefined): AgGrid.CellStyle {
  switch (status?.toUpperCase()) {
    case "PENDING":
      return { backgroundColor: "#DBEAFE", color: "#1E40AF", fontWeight: "600" };
    case "READY":
      return { backgroundColor: "#F3F4F6", color: "#374151", fontWeight: "600" };
    case "PUBLISHED":
      return { backgroundColor: "#D1FAE5", color: "#065F46", fontWeight: "600" };
    case "PUBLISH_FAILED":
      return { backgroundColor: "#FEF3C7", color: "#92400E", fontWeight: "600" };
    case "GENERATION_FAILED":
      return { backgroundColor: "#FEE2E2", color: "#991B1B", fontWeight: "600" };
    default:
      return {};
  }
}

export default class extends GridController<SettlementReport> {
  static targets = ["grid", "fromInput", "toInput", "downloadButton"];
  static values = {
    downloadUrl: String,
  };

  declare readonly gridTarget: HTMLInputElement;
  declare readonly fromInputTarget: HTMLInputElement;
  declare readonly toInputTarget: HTMLInputElement;
  declare readonly downloadButtonTarget: HTMLButtonElement;
  declare readonly hasDownloadButtonTarget: boolean;
  declare readonly downloadUrlValue: string;

  private appliedFrom = "";
  private appliedTo = "";
  private downloading = false;

  connect() {
    super.connect();
    this.updateDownloadButton();
  }

  applyPeriodFilter(): void {
    this.appliedFrom = this.fromInputTarget?.value || "";
    this.appliedTo = this.toInputTarget?.value || "";
    this.refreshGrid();
  }

  async downloadSelected(): Promise<void> {
    if (!this.gridApi || this.downloading) return;

    const fileIds = this.gridApi
      .getSelectedRows()
      .filter((row) => row.downloadable)
      .map((row) => row.id);

    await this.downloadFiles(fileIds);
  }

  private async downloadFiles(fileIds: string[]): Promise<void> {
    if (!fileIds.length) return;

    this.downloading = true;
    this.updateDownloadButton();

    try {
      const response = await http.httpPostBlob(this.downloadUrlValue, { file_ids: fileIds });
      if (response.ok) {
        await triggerBrowserDownload(response, fileIds.length > 1 ? "settlement-reports.zip" : "report.csv");
      } else {
        notifications.alert(await this.downloadErrorMessage(response));
      }
    } finally {
      this.downloading = false;
      this.updateDownloadButton();
    }
  }

  private async downloadErrorMessage(response: Response): Promise<string> {
    const parsed = await parseError(response);
    if (parsed) return parsed;

    try {
      const i18n = JSON.parse(this.gridTarget.dataset.i18nJson || "{}");
      return i18n.download_failed || "Download failed";
    } catch {
      return "Download failed";
    }
  }

  private refreshGrid(): void {
    if (!this.gridApi) return;
    this.gridApi.paginationGoToPage(0);
    this.gridApi.deselectAll();
    this.gridApi.purgeInfiniteCache();
    this.updateDownloadButton();
  }

  private updateDownloadButton(): void {
    if (!this.hasDownloadButtonTarget) return;

    const selectedCount = this.gridApi?.getSelectedRows().filter((row) => row.downloadable).length || 0;
    this.downloadButtonTarget.disabled = this.downloading || selectedCount === 0;
  }

  private normalizeDate(value: string, boundary: "start" | "end"): string {
    if (!value) return "";
    return boundary === "start" ? `${value}T00:00:00` : `${value}T23:59:59`;
  }

  override buildGrid() {
    const ctrl = this;
    const { i18nJson, paginationPageSize } = this.gridTarget.dataset;
    const i18n = JSON.parse(i18nJson);

    const statusValues = ["PENDING", "READY", "PUBLISHED", "PUBLISH_FAILED", "GENERATION_FAILED"];
    const statusLabels = Object.fromEntries(
      statusValues.map((code) => [code, i18n[`status_${code.toLowerCase()}`] || code]),
    );

    const colDefs: AgGrid.ColDef<SettlementReport>[] = [
      {
        width: 48,
        checkboxSelection: true,
        headerCheckboxSelection: false,
        headerComponent: SelectDownloadableRows,
        sortable: false,
        resizable: false,
        suppressHeaderMenuButton: true,
      },
      {
        field: "reporting_period",
        ...DEFAULT_COL_DEF,
        sortable: false,
        filter: false,
      },
      {
        field: "file_name",
        ...DEFAULT_COL_DEF,
        sortable: false,
        filter: false,
      },
      {
        field: "status",
        ...DEFAULT_COL_DEF,
        sortable: false,
        filter: false,
        valueFormatter: (params) => {
          const code = params.data?.status_code?.toUpperCase();
          const label = statusLabels[code] || params.value || code;
          if (code === "PENDING") {
            return `${label} ⏳`;
          }
          return label;
        },
        tooltipValueGetter: (params) => params.data?.error_message || "",
        cellStyle: (params) => statusCellStyle(params.data?.status_code),
      },
      {
        ...ACTIONS_COL_DEF,
        cellClass: "actions-cell",
        onCellClicked: (params) => {
          const target = params.event?.target as HTMLElement;
          const button = target?.tagName?.toLowerCase() === "button" ? target : target?.closest("button");
          if (button?.dataset?.action === "download" && params.data?.downloadable) {
            ctrl.downloadFiles([params.data.id]);
          }
        },
      },
    ];

    const dataSource = new datasource.Datasource({
      callbacks: {
        appendQueryParams: (url) => {
          if (ctrl.appliedFrom) {
            url.searchParams.set("from", ctrl.normalizeDate(ctrl.appliedFrom, "start"));
          } else {
            url.searchParams.delete("from");
          }
          if (ctrl.appliedTo) {
            url.searchParams.set("to", ctrl.normalizeDate(ctrl.appliedTo, "end"));
          } else {
            url.searchParams.delete("to");
          }
        },
      },
      limit: paginationPageSize,
    });

    const { maxBlocksInCache, onCellFocused, ...defaultOptions } = gridUtils.DEFAULT_GRID_OPTIONS;

    const options: AgGrid.GridOptions<SettlementReport> = {
      ...defaultOptions,
      defaultColDef: {
        ...(gridUtils.DEFAULT_GRID_OPTIONS.defaultColDef as AgGrid.ColDef<SettlementReport, any>),
      },
      datasource: dataSource,
      cacheBlockSize: parseInt(paginationPageSize),
      columnDefs: gridUtils.buildColumnDefs(colDefs, { i18n }),
      pagination: true,
      paginationPageSize: parseInt(paginationPageSize),
      rowSelection: "multiple",
      suppressRowClickSelection: true,
      isRowSelectable: (node) => node.data?.downloadable === true,
      onRowSelected: () => ctrl.updateDownloadButton(),
    };

    const gridApi = AgGrid.createGrid(this.gridTarget, options);
    dataSource.gridApi = gridApi;
    return gridApi;
  }
}
